/**
 * Vom Auftragszustand auf das Vokabular des Entwurfs.
 *
 * Der Server führt sieben Auftragszustände (A5), der Entwurf zeichnet seine
 * Punkte und Marken über `Stand` aus `entwurf-zustaende.ts`. Die Abbildung ist
 * die einzige Stelle, an der beide Vokabulare sich berühren — genau wie
 * `zustandAusGruppe` für Aufgaben.
 *
 * `fertig_mit_aufhaltern` bekommt `blockiert`, nicht `gescheitert`: Der
 * Auftrag ist durchgelaufen, es hält nur etwas auf. Der Unterschied ist die
 * ganze Auskunft dieser Seite, und er darf beim Übersetzen nicht verloren
 * gehen.
 */

import type { Stand } from "@/lib/entwurf-zustaende";

export type Auftragszustand =
  | "gebremst"
  | "wartet"
  | "laeuft"
  | "fertig"
  | "fertig_mit_aufhaltern"
  | "gescheitert"
  | "abgebrochen";

const STAND_JE_ZUSTAND: Readonly<Record<Auftragszustand, Stand>> = {
  gebremst: "gebremst",
  wartet: "wartet",
  laeuft: "laeuft",
  fertig: "ausgefuehrt",
  fertig_mit_aufhaltern: "blockiert",
  gescheitert: "gescheitert",
  abgebrochen: "abgebrochen",
};

export function standAusAuftragszustand(zustand: string): Stand {
  return STAND_JE_ZUSTAND[zustand as Auftragszustand] ?? "ausstehend";
}

/**
 * Die Beschriftung, die B4 vorschreibt.
 *
 * Nicht die aus `STAND`: Dort heißt `blockiert` „Blockiert", hier muss dasselbe
 * Feld „Hält auf" sagen — das Wort, das die Übersicht benutzt.
 */
const MARKE: Readonly<Record<Auftragszustand, string>> = {
  gebremst: "Sammelt",
  wartet: "In Warteschlange",
  laeuft: "Läuft",
  fertig: "Sauber",
  fertig_mit_aufhaltern: "Hält auf",
  gescheitert: "Gescheitert",
  abgebrochen: "Abgebrochen",
};

export function auftragsMarke(zustand: string): string {
  return MARKE[zustand as Auftragszustand] ?? zustand;
}

/** Die Farbe der Marke — dasselbe Vokabular wie der Tracker. */
export function auftragsFarbe(
  zustand: string,
): "gruen" | "rot" | "gelb" | "blau" | "grau" {
  switch (zustand as Auftragszustand) {
    case "laeuft":
      return "blau";
    case "fertig":
      return "gruen";
    case "fertig_mit_aufhaltern":
      return "gelb";
    case "gescheitert":
      return "rot";
    default:
      return "grau";
  }
}

/** Stand eines Schritts innerhalb der Kette (B3, Punkt 4). */
export function standAusSchritt(
  zustand: string,
  ergebnis: string | null,
): Stand {
  switch (zustand) {
    case "laeuft":
      return "laeuft";
    case "uebersprungen":
      return "uebersprungen";
    case "gescheitert":
      return "gescheitert";
    case "fertig":
      return ergebnis === "aufhalter" ? "blockiert" : "ausgefuehrt";
    default:
      return "ausstehend";
  }
}

/** Ergebnis eines Schritts im Klartext — für den Tooltip der Kette. */
export function schrittErgebnisText(
  zustand: string,
  ergebnis: string | null,
): string {
  if (zustand === "uebersprungen") return "übersprungen";
  if (zustand === "laeuft") return "läuft";
  if (zustand === "ausstehend") return "ausstehend";
  switch (ergebnis) {
    case "sauber":
      return "sauber";
    case "aufhalter":
      return "hält auf";
    case "nicht_bewertbar":
      return "nicht bewertbar";
    default:
      return zustand;
  }
}

/**
 * „vor 4 Min" — relativ, weil die Frage „wie frisch ist das" heißt (B3).
 *
 * Das genaue Datum gehört ins `title`-Attribut daneben, nicht in die Zeile.
 */
export function vorZeit(
  wert: string | Date | null | undefined,
  jetzt = Date.now(),
): string {
  if (!wert) return "—";
  const minuten = Math.max(
    0,
    Math.floor((jetzt - new Date(wert).getTime()) / 60_000),
  );
  if (minuten < 1) return "gerade eben";
  if (minuten < 60) return `vor ${minuten} Min.`;
  const stunden = Math.floor(minuten / 60);
  if (stunden < 24) return `vor ${stunden} Std.`;
  const tage = Math.floor(stunden / 24);
  return tage === 1 ? "vor 1 Tag" : `vor ${tage} Tagen`;
}

/**
 * Der Countdown der Beruhigungsfrist — „noch 2:10" (B4).
 *
 * Läuft rückwärts und wird nie negativ: Eine abgelaufene Frist zeigt „startet
 * gleich", nicht „noch -0:03".
 */
export function beruhigungText(
  gebremstBis: string | Date | null | undefined,
  jetzt = Date.now(),
): string {
  if (!gebremstBis) return "Sammelt Änderungen";
  const rest = new Date(gebremstBis).getTime() - jetzt;
  if (rest <= 0) return "Sammelt Änderungen — startet gleich";
  const sekunden = Math.floor(rest / 1_000);
  const minuten = Math.floor(sekunden / 60);
  const zweistellig = String(sekunden % 60).padStart(2, "0");
  return `Sammelt Änderungen — startet in ${minuten}:${zweistellig}`;
}
